import { faCube } from "@fortawesome/pro-regular-svg-icons";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { Outlet, useMatch } from "react-router-dom";

import { Flow } from "src/api/flowTypes";
import { useFlow } from "src/api/queries";
import { LoadingView } from "src/base-components/LoadingView";
import { EmptyState } from "src/design-system/EmptyState";
import { FlowPageLayout } from "src/flow/FlowPageLayout";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import {
  useFlowContext,
  useWorkspaceContext,
} from "src/router/routerContextHooks";
import {
  FlowPageParams,
  getUrlToWsDashboard,
  WsDashboardPageCode,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type FlowPageContext = ReturnType<typeof useFlowContext>;

export type FlowPageContextNoData = Partial<FlowPageContext>;

export const FlowPage: React.FC = () => {
  const { flow_id, wsId, orgId } = useParamsDecode<FlowPageParams>();
  const flow = useFlow(flow_id, {
    refetchOnMount: true,
  });
  const { workspace, modules } = useWorkspaceContext();
  const isVersionsPage = useMatch("/org/:orgId/ws/:wsId/flows/:flow_id");
  const isReviewCasePage = useMatch(
    "/org/:orgId/ws/:wsId/flows/:flow_id/review-queue/:case_id",
  );
  const isJobPage = useMatch(
    "/org/:orgId/ws/:wsId/flows/:flow_id/jobs/:job_id",
  );

  useDocumentTitle(
    flow.data?.name ? [flow.data.name, "Decision Flows"] : ["Decision Flows"],
  );

  if (flow.isError) {
    return (
      <div className="flex w-full items-center justify-center">
        <EmptyState
          actionButton={{
            icon: faArrowLeft,
            text: "Go to Home Page",
            linkTo: getUrlToWsDashboard({
              orgId,
              wsId,
              page: WsDashboardPageCode.Flows,
            }),
          }}
          dataLoc="flow-not-found"
          description="To see all Decision Flows, please go to the Home page."
          headline="This Decision Flow was not found"
          icon={faCube}
        />
      </div>
    );
  }

  const isStandalonePage = isReviewCasePage || isJobPage;

  if (isStandalonePage) {
    return (
      <LoadingView
        queryResult={flow}
        renderUpdated={(flow: Flow) => (
          <Outlet context={{ flow, workspace, modules, orgId }} />
        )}
      />
    );
  }

  return (
    <FlowPageLayout flow={flow.data}>
      <LoadingView
        queryResult={flow}
        renderUpdated={(flow: Flow) => (
          <Outlet context={{ flow, workspace, modules, orgId }} />
        )}
        renderUpdating={() =>
          isVersionsPage ? (
            <Outlet context={{ workspace, modules, orgId }} />
          ) : (
            <></>
          )
        }
      />
    </FlowPageLayout>
  );
};
